import React, { useState } from "react";
import { Helmet } from "react-helmet";
import {
  FiArrowRight,
  FiCheckCircle,
  FiClock,
  FiCpu,
  FiLayers,
  FiMessageSquare,
  FiSend,
  FiZap,
} from "react-icons/fi";

import Header from "../../../components/Header";
import Footer from "../../../components/Footer";

import "./BookDemo.css";

const BookDemo = () => {
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    role: "",
    companySize: "",
    interest: "",
    message: "",
  });

  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    setSubmitted(true);

    setFormData({
      name: "",
      company: "",
      email: "",
      phone: "",
      role: "",
      companySize: "",
      interest: "",
      message: "",
    });
  };

  const handleNewDemo = () => {
    setSubmitted(false);
  };

  return (
    <>
      <Helmet>
        <title>Book a Demo | Dropgear IO</title>

        <meta
          name="description"
          content="Book a demo with Dropgear IO and explore AI solutions, intelligent automation, AI agents, and workflow transformation."
        />
      </Helmet>

      <Header />

      <main className="dg-demo-page">

        {/* =====================================================
            HERO
        ===================================================== */}

        <section className="dg-demo-hero">

          <div className="dg-demo-container">

            <div className="dg-demo-hero-content">

              <span className="dg-demo-eyebrow">
                BOOK A DEMO
              </span>

              <h1>
                See what AI can
                <span> do for your workflow.</span>
              </h1>

              <p>
                Explore how Dropgear IO can help your team automate repetitive
                work, connect intelligent systems, and move important
                workflows forward.
              </p>

            </div>

          </div>

        </section>


        {/* =====================================================
            MAIN DEMO SECTION
        ===================================================== */}

        <section className="dg-demo-main">

          <div className="dg-demo-container dg-demo-grid">

            {/* =================================================
                LEFT SIDE
            ================================================= */}

            <div className="dg-demo-info">

              <span className="dg-demo-eyebrow">
                WHAT TO EXPECT
              </span>

              <h2>
                A demo built around
                <span> your business.</span>
              </h2>

              <p className="dg-demo-description">
                We don't want the conversation to be just another product
                walkthrough. We'll focus on your workflows, challenges, and
                where intelligent automation could create practical value.
              </p>


              {/* DEMO BENEFITS */}

              <div className="dg-demo-benefits">

                <div className="dg-demo-benefit">

                  <div className="dg-demo-benefit-icon">
                    <FiCpu />
                  </div>

                  <div>
                    <h3>
                      Explore AI Capabilities
                    </h3>

                    <p>
                      See how AI solutions and intelligent systems can fit
                      into real business workflows.
                    </p>
                  </div>

                </div>


                <div className="dg-demo-benefit">

                  <div className="dg-demo-benefit-icon">
                    <FiZap />
                  </div>

                  <div>
                    <h3>
                      Identify Automation Opportunities
                    </h3>

                    <p>
                      Discuss repetitive processes that could potentially
                      be simplified or automated.
                    </p>
                  </div>

                </div>


                <div className="dg-demo-benefit">

                  <div className="dg-demo-benefit-icon">
                    <FiLayers />
                  </div>

                  <div>
                    <h3>
                      Understand the Approach
                    </h3>

                    <p>
                      Learn how Dropgear IO approaches AI implementation,
                      integrations, and workflow design.
                    </p>
                  </div>

                </div>


                <div className="dg-demo-benefit">

                  <div className="dg-demo-benefit-icon">
                    <FiMessageSquare />
                  </div>

                  <div>
                    <h3>
                      Discuss Your Use Case
                    </h3>

                    <p>
                      Bring a specific challenge or workflow and we'll
                      explore possible approaches together.
                    </p>
                  </div>

                </div>

              </div>


              {/* DEMO NOTE */}

              <div className="dg-demo-note">

                <FiClock />

                <div>

                  <strong>
                    Keep it practical.
                  </strong>

                  <p>
                    The goal is to understand your requirements and identify
                    where Dropgear IO could be useful.
                  </p>

                </div>

              </div>

            </div>


            {/* =================================================
                RIGHT FORM
            ================================================= */}

            <div className="dg-demo-form-card">

              {!submitted ? (

                <>
                  <div className="dg-demo-form-heading">

                    <span>
                      REQUEST YOUR DEMO
                    </span>

                    <h3>
                      Tell us about yourself
                    </h3>

                    <p>
                      Share a few details and our team will connect with you
                      to arrange the next step.
                    </p>

                  </div>


                  <form
                    className="dg-demo-form"
                    onSubmit={handleSubmit}
                  >

                    {/* NAME + COMPANY */}

                    <div className="dg-demo-form-row">

                      <div className="dg-demo-field">

                        <label htmlFor="dg-demo-name">
                          Your Name *
                        </label>

                        <input
                          id="dg-demo-name"
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Enter your name"
                          required
                        />

                      </div>


                      <div className="dg-demo-field">

                        <label htmlFor="dg-demo-company">
                          Company *
                        </label>

                        <input
                          id="dg-demo-company"
                          type="text"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          placeholder="Company name"
                          required
                        />

                      </div>

                    </div>


                    {/* EMAIL + PHONE */}

                    <div className="dg-demo-form-row">

                      <div className="dg-demo-field">

                        <label htmlFor="dg-demo-email">
                          Work Email *
                        </label>

                        <input
                          id="dg-demo-email"
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="you@company.com"
                          required
                        />

                      </div>


                      <div className="dg-demo-field">

                        <label htmlFor="dg-demo-phone">
                          Phone Number
                        </label>

                        <input
                          id="dg-demo-phone"
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="+91 00000 00000"
                        />

                      </div>

                    </div>


                    {/* ROLE + COMPANY SIZE */}

                    <div className="dg-demo-form-row">

                      <div className="dg-demo-field">

                        <label htmlFor="dg-demo-role">
                          Your Role
                        </label>

                        <select
                          id="dg-demo-role"
                          name="role"
                          value={formData.role}
                          onChange={handleChange}
                        >

                          <option value="">
                            Select your role
                          </option>

                          <option value="Founder / Owner">
                            Founder / Owner
                          </option>

                          <option value="CEO / Executive">
                            CEO / Executive
                          </option>

                          <option value="Operations">
                            Operations
                          </option>

                          <option value="Technology / IT">
                            Technology / IT
                          </option>

                          <option value="Product">
                            Product
                          </option>

                          <option value="Finance">
                            Finance
                          </option>

                          <option value="Other">
                            Other
                          </option>

                        </select>

                      </div>


                      <div className="dg-demo-field">

                        <label htmlFor="dg-demo-company-size">
                          Company Size
                        </label>

                        <select
                          id="dg-demo-company-size"
                          name="companySize"
                          value={formData.companySize}
                          onChange={handleChange}
                        >

                          <option value="">
                            Select company size
                          </option>

                          <option value="1-10">
                            1–10 employees
                          </option>

                          <option value="11-50">
                            11–50 employees
                          </option>

                          <option value="51-200">
                            51–200 employees
                          </option>

                          <option value="201-500">
                            201–500 employees
                          </option>

                          <option value="500+">
                            500+ employees
                          </option>

                        </select>

                      </div>

                    </div>


                    {/* INTEREST */}

                    <div className="dg-demo-field">

                      <label htmlFor="dg-demo-interest">
                        What would you like to explore? *
                      </label>

                      <select
                        id="dg-demo-interest"
                        name="interest"
                        value={formData.interest}
                        onChange={handleChange}
                        required
                      >

                        <option value="">
                          Select an area
                        </option>

                        <option value="AI Solutions">
                          AI Solutions
                        </option>

                        <option value="AI Automation">
                          AI Automation
                        </option>

                        <option value="AI Agents">
                          AI Agents
                        </option>

                        <option value="Workflow Automation">
                          Workflow Automation
                        </option>

                        <option value="Industry Solutions">
                          Industry Solutions
                        </option>

                        <option value="Other">
                          Other
                        </option>

                      </select>

                    </div>


                    {/* MESSAGE */}

                    <div className="dg-demo-field">

                      <label htmlFor="dg-demo-message">
                        What would you like to achieve?
                      </label>

                      <textarea
                        id="dg-demo-message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell us about your workflow, challenge, or AI use case..."
                        rows="5"
                      ></textarea>

                    </div>


                    {/* SUBMIT */}

                    <button
                      type="submit"
                      className="dg-demo-submit"
                    >

                      <span>
                        Request a Demo
                      </span>

                      <FiArrowRight />

                    </button>


                    <p className="dg-demo-disclaimer">
                      Submit your details and our team will contact you
                      regarding your demo request.
                    </p>

                  </form>
                </>

              ) : (

                /* =================================================
                   SUCCESS MESSAGE
                ================================================= */

                <div className="dg-demo-success">

                  <div className="dg-demo-success-icon">
                    <FiCheckCircle />
                  </div>

                  <span className="dg-demo-success-label">
                    DEMO REQUEST RECEIVED
                  </span>

                  <h3>
                    Thank you for your interest!
                  </h3>

                  <p>
                    Your demo request has been received successfully.
                    Our team will review your details and connect with you
                    shortly to arrange your Dropgear IO demo.
                  </p>

                  <div className="dg-demo-success-info">

                    <FiClock />

                    <span>
                      We look forward to learning about your workflow and
                      showing you how Dropgear IO can help.
                    </span>

                  </div>

                  <button
                    type="button"
                    className="dg-demo-new-request"
                    onClick={handleNewDemo}
                  >
                    Request another demo
                    <FiArrowRight />
                  </button>

                </div>

              )}

            </div>

          </div>

        </section>


        {/* =====================================================
            BOTTOM CTA
        ===================================================== */}

        <section className="dg-demo-bottom">

          <div className="dg-demo-container">

            <div className="dg-demo-bottom-card">

              <div>

                <span className="dg-demo-eyebrow">
                  EXPLORE FIRST
                </span>

                <h2>
                  Want to understand
                  <span> Dropgear IO first?</span>
                </h2>

                <p>
                  Explore our AI solutions, automation capabilities, agents,
                  and industry-focused workflows.
                </p>

              </div>

              <a
                href="/ai-solutions"
                className="dg-demo-bottom-button"
              >
                Explore AI Solutions
                <FiArrowRight />
              </a>

            </div>

          </div>

        </section>

      </main>

      <Footer />
    </>
  );
};

export default BookDemo;

